import { useState, useEffect, useMemo, ComponentType, forwardRef, createElement, useContext, useCallback } from 'react'
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'

// evertel
import { SessionService } from '@evertel/session/SessionService'
import { useService } from '@evertel/di'
import { DisplayRoomMessage, DisplayThreadMessage } from '@evertel/stores'
import { useVirtuosoScrollSaveState } from '@evertel/web/ui'
import { MessageWallContext } from '@evertel/message'
import { Message } from './Message'
import { DateBlock } from './elements'

interface MessageListProps {
    modelId: number
    modelType: 'room' | 'thread'
    repliesToId: number
    listRef?: any // ref for the windowed-list component
    listHeaderComponent?: ComponentType<{ context: any }> // component to display at top of list
    isLoading?: boolean // is data loading. useful for paging/lazy-loading
    onBottomStateChange?: () => void

    // Windowed List Props
    messages: (DisplayRoomMessage | DisplayThreadMessage)[] // loaded data
    groups?: string[]
    groupCounts?: number[]
    renderItem?: () => void // callback for each item to be rendered. callback includes {index, isScrolling, style}
    onStartReached?: () => void
    onEndReached?: () => void
    onAtBottomStateChange?: (atBottom: boolean) => void
    className?: string // applied to outer container of list
}

const INITIAL_MESSAGE_START = 1000000

const MessageList = observer(forwardRef<VirtuosoHandle, MessageListProps>(({
    modelId,
    modelType,
    repliesToId,
    messages = [],
    onStartReached,
    onEndReached,
    onAtBottomStateChange,
    listHeaderComponent
}, listRef) => {

    const sessionService = useService(SessionService)

    const {
        focusedMessageId,
        setFocusedMessageId,
        messageWallController,
        jumpToMessageId,
        setJumpToMessageId
    } = useContext(MessageWallContext)

    const activeView = messageWallController?.activeView || 'primary'

    // Defer followOutput activation until after Virtuoso has rendered with the
    // new dataset. Without this, a merge (anchor→primary) enables followOutput
    // in the same tick as the dataset expansion — Virtuoso's stale atBottom
    // state triggers a cascade scroll to the bottom.
    const [followEnabled, setFollowEnabled] = useState(activeView === 'primary')

    useEffect(() => {
        if (activeView === 'primary') {
            const raf = requestAnimationFrame(() => setFollowEnabled(true))
            return () => cancelAnimationFrame(raf)
        } else {
            setFollowEnabled(false)
        }
    }, [activeView])

    // Two keys: one for Virtuoso's React `key` (controls remount), one for scroll state save/restore.
    // `virtuosoKey` excludes `activeView` so a merge (anchor → primary) doesn't remount the component.
    // `stateKey` includes `activeView` so the hook saves/restores separate scroll positions per view.
    const virtuosoKey = `${modelType}-${modelId}-${repliesToId}`
    const stateKey = `${modelType}-${modelId}-${repliesToId}-${activeView}`

    const {
        virtuosoRef,
        setScrollerRef,
        getRestoreState,
        keyDownCallback,
        handleVirtuosoFocus,
        handleVirtuosoBlur
    } = useVirtuosoScrollSaveState({key: stateKey, messages, focusedMessageId, setFocusedMessageId})

    const [initialDate, setInitialDate] = useState<Date | null>(null)

    useEffect(() => {
        if (initialDate === null && messages.length > 0) {
            //grab the newest date from the end of the data
            setInitialDate(messages[messages.length - 1].createdDate)
        }
        return () => {
            //saving the state here fails as virtuoso has already deconstructed
        }
    }, [messages, initialDate])

    useEffect(() => {
        setInitialDate(null)
    }, [modelType, modelId, repliesToId])

    // Jump to a specific message: scroll to center, highlight, and set focus
    useEffect(() => {
        if (!jumpToMessageId || !messages.length) return

        const index = messages.findIndex(m => m.id === jumpToMessageId)
        if (index === -1) return

        // Set keyboard focus to the jumped-to message so arrow keys navigate from there
        setFocusedMessageId?.(jumpToMessageId)

        // Small delay to let Virtuoso finish rendering after data change
        const scrollTimer = setTimeout(() => {
            virtuosoRef.current?.scrollToIndex({
                index,
                align: 'center',
                behavior: 'auto'
            })
        }, 100)

        // Clear jump state after highlight animation completes, also resets for re-jump to same message
        const clearTimer = setTimeout(() => {
            setJumpToMessageId?.(null)
        }, 2500)

        return () => {
            clearTimeout(scrollTimer)
            clearTimeout(clearTimer)
        }
    }, [jumpToMessageId, messages.length])

    const [firstItemIndex, initialTopMostItemIndex, initialMessageIndex] = useMemo(() => {
        if (initialDate && messages.length) {
            // Key off initialDate on all new message loads so virtuoso properly keeps track of new items
            // @ts-expect-error findLastIndex is too new for our ts config
            const indexedDate = messages.findLastIndex(msg => msg.createdDate <= initialDate)
            return [INITIAL_MESSAGE_START - indexedDate, messages.length - 1, indexedDate]
        }
        return [INITIAL_MESSAGE_START, 0]
    }, [messages, initialDate])

    const localStartReached = () => {
        onStartReached()
    }

    const itemContent = useCallback((index: number, msg: DisplayRoomMessage) => {
        return (
            <MessageMe
                msg={msg}
                modelType={modelType}
                isScrolling={false}
                index={index}
                isFocused={msg.id === focusedMessageId}
                isJumpTarget={msg.id === jumpToMessageId}
                setFocusedMessageId={setFocusedMessageId}
            />
        )
    }, [modelType, focusedMessageId, jumpToMessageId, setFocusedMessageId])

    if (!messages.length || !initialDate) {
        return (
            <div style={{ height: '100%', overflowY: 'auto' }}>
                {createElement(listHeaderComponent)}
            </div>
        )
    }

    const restoreState = getRestoreState()

    //skipAnimationFrame crashes cypress tests and is only for UI so ok to disabled for tests
    const skipAnimationFrameInResizeObserver = true && !sessionService.testEnabled

    // // Discussion about jumping: https://github.com/petyosi/react-virtuoso/discussions/1083
    return (
        <Virtuoso
            id="virtuoso-scroller"
            //logLevel={LogLevel.DEBUG}

            key={virtuosoKey}
            computeItemKey={(key, msg) => `item-${msg.id}`}
            ref={setRefs(virtuosoRef, listRef)}
            // // Save/restore works and scrolls to last location:
            // // Issues: 1) won't scroll for very bottom items
            // //         2) offset broken when header exists
            restoreStateFrom={restoreState}
            role="list"
            aria-label="Message list"
            style={{ outline: 'none' }}
            onKeyDown={keyDownCallback}
            onFocus={handleVirtuosoFocus}
            onBlur={handleVirtuosoBlur}
            // itemContent={(index, msg) => {
            //     return ( <div><b>{msg.ownerId}</b><br />{msg.text}</div> )
            // }}
            itemContent={itemContent}
            scrollerRef={(ref) => { setScrollerRef(ref as HTMLElement) }}

            // // old method to save on scroll:
            // rangeChanged={(range) => saveState() }

            // context={{ isScrolling }}

            data={messages}
            firstItemIndex={firstItemIndex}
            // // can't have both restore and initialTopMostItemIndex at the same time
            {...(!restoreState && { initialTopMostItemIndex: initialTopMostItemIndex })}
            // alignToBottom={true}

            startReached={localStartReached}
            endReached={onEndReached}

            // // https://github.com/petyosi/react-virtuoso/issues/1096#issuecomment-2262255320
            skipAnimationFrameInResizeObserver={skipAnimationFrameInResizeObserver}
            // // single msg w/o header: 38px tall
            // // msg w/ header: 61px tall
            defaultItemHeight={38}
            increaseViewportBy={{ top: 800, bottom: 300 }} // // number of px above current view to pre-render
            atBottomThreshold={350} // // scroll to bottom if within this many px on new message
            atBottomStateChange={onAtBottomStateChange}
            // Only auto-scroll to bottom on new messages when viewing the live primary window
            followOutput={followEnabled ? 'smooth' : false}
            // isScrolling={setIsScrolling} //causes many re-loads
            components={{
                // Header: () => { return <h1 style={{ height: 100 }}> hello</ h1> }
                Header: listHeaderComponent
            }}
        />
    )
}))

// const mountCount = []
interface MessageMeProps {
    msg: DisplayRoomMessage
    modelType: 'room' | 'thread'
    isScrolling: boolean
    index: number
    isFocused: boolean
    isJumpTarget: boolean
    setFocusedMessageId: (id: number | null) => void
}

const MessageMe: React.FC<MessageMeProps> = ({
    msg,
    modelType,
    isScrolling,
    index,
    isFocused,
    isJumpTarget,
    setFocusedMessageId
}) => {
    //for testing
    // useEffect(() => {
    //     mountCount[index] = (mountCount[index] ?? 0) + 1
    // }, [])
    // const renderCount = useRef(0)
    // renderCount.current += 1

    // if (!msg) return null

    return (
        <>
            {/* Must collapse border with wrapper or scrolling breaks
                Must also have tabIndex=-1 so clicking the date is selectable
                and unaffected by the keyboard nav focus handlers
             */}
            {msg.dateChanged && (
                <div style={{ padding: '1px' }} tabIndex={-1}>
                    <DateBlock
                        date={msg.publishedDate || msg.createdDate}
                    />
                </div>
            )}
            <div
                id={`message-id-${msg.id}`}
                className={classNames({ 'message-highlight': isJumpTarget })}
                role="listitem"
                tabIndex={-1}
                aria-label={`Message from ${msg.ownerId || 'Unknown'}`}
                aria-current={isFocused ? 'true' : undefined}
                style={{ padding: '1px' }}
                data-message-holder
                onMouseDown={() => {setFocusedMessageId(msg.id)}}
            >
                {/* <h3>Render#:{renderCount.current}</h3> */}
                {/* <h1>{msg.ownerId} --- {msg.id}</h1> */}
                {/* <h3>Mount#:{mountCount[index]}</h3> */}
                {/* <p>{JSON.stringify(msg)}</p> */}
                {/* <p>{msg.text}</p> */}

                <Message
                    message={msg}
                    modelType={modelType}
                    isScrolling={isScrolling}
                    showHoverBar={!msg.isRetracted}
                />
            </div>
        </>
    )
}

/*
 * helper method to set multiple refs when using forwardRef but
 * a local ref is still needed.  No way to tell if the passed ref
 * is a ref or a callback function
 */
function setRefs(...refs) {
    return (instance) => {
        refs.forEach(ref => {
            if (typeof ref === 'function') {
                ref(instance)
            } else if (ref) {
                ref.current = instance
            }
        })
    }
}

MessageList.displayName = 'MessageList'

export { MessageList }
